/**
 * FormPreview — a live, interactive preview of the attendee form, rendered from
 * the in-progress event draft using the exact same public components
 * (ItemPicker + FieldRenderer). Mirrors how Typeform/Jotform show the form as
 * you build it. Submit is disabled — this is a preview only.
 */
import React, { useState } from 'react';
import FieldRenderer from './FieldRenderer';
import ItemPicker from './ItemPicker';
import { normalizeUrl, orderTotal, formatMoney } from '../eventUtils';
import { t } from '../strings';

export default function FormPreview({ event }) {
  const [qty, setQty] = useState({});
  const [ans, setAns] = useState({});

  const s = event.settings || {};
  const items = event.menuItems || [];
  const fields = event.fields || [];
  const accent = event.bannerColor || '#f59e0b';
  const subtotal = orderTotal(event, qty);

  return (
    <div className="ev-preview" style={{ '--ev-accent': accent }}>
      <div className="ev-preview__phone">
        <div className="evf__banner">
          <h1 className="evf__title">{event.title || 'Event title'}</h1>
          {event.description ? <p className="evf__desc">{event.description}</p> : null}
          {s.payNote ? <p className="evf__paynote">💳 {s.payNote}</p> : null}
        </div>

        <div className="evf__section">
          <h2 className="evf__section-title">{t('yourDetails')}</h2>
          <div className="evf-field">
            <label className="evf-label">{t('name')}{s.requireName !== false ? <span className="evf-req">*</span> : null}</label>
            <input className="evf-input" disabled placeholder={t('name')} />
          </div>
          {s.requirePhone && (
            <div className="evf-field">
              <label className="evf-label">{t('phone')}<span className="evf-req">*</span></label>
              <input className="evf-input" disabled placeholder={t('phone')} />
            </div>
          )}
          {s.requireEmail && (
            <div className="evf-field">
              <label className="evf-label">{t('email')}<span className="evf-req">*</span></label>
              <input className="evf-input" disabled placeholder="you@example.com" />
            </div>
          )}
        </div>

        {(items.length > 0 || event.menuLink) && (
          <div className="evf__section">
            <h2 className="evf__section-title">{items.length > 0 ? t('chooseItems') : t('yourOrder')}</h2>
            {event.menuLink ? (
              <a className="evf__menulink" href={normalizeUrl(event.menuLink)} onClick={(e) => e.preventDefault()}>
                📋 {t('viewMenu')}
              </a>
            ) : null}
            {items.length > 0 ? (
              <ItemPicker items={items} quantities={qty} onChange={(id, q) => setQty((p) => ({ ...p, [id]: q }))} showPrices={s.showPrices} />
            ) : (
              <textarea className="evf-input" rows={2} disabled placeholder={t('yourOrderPlaceholder')} />
            )}
          </div>
        )}

        {fields.length > 0 && (
          <div className="evf__section">
            {fields.map((f) => (
              <FieldRenderer key={f.id} field={f} value={ans[f.id]} onChange={(v) => setAns((p) => ({ ...p, [f.id]: v }))} />
            ))}
          </div>
        )}

        {s.allowInstructions && (
          <div className="evf__section">
            <div className="evf-field">
              <label className="evf-label">{t('specialInstructions')}</label>
              <textarea className="evf-input" rows={2} disabled placeholder={t('instructionsPlaceholder')} />
            </div>
          </div>
        )}

        <button className="evf__submit" disabled>
          {s.showPrices && subtotal > 0 ? `${t('submit')} · ${formatMoney(subtotal)}` : t('submit')}
        </button>
      </div>
    </div>
  );
}
